
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Benefit {
  icon: string;
  title: string;
  description: string;
}

interface AnimatedCardProps {
  benefit: Benefit;
  index: number;
}

/**
 * Card that animates into view when it becomes visible in the viewport.
 * Includes robust cleanup and error handling for the IntersectionObserver.
 */
function AnimatedCard({ benefit, index }: AnimatedCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Guard against browsers that do not support IntersectionObserver
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);

    // Cleanup on unmount
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${index * 120}ms` }}
      className={`bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-7 shadow-sm hover:shadow-lg transition-all duration-700 hover:-translate-y-1
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className="w-9 h-9 sm:w-12 sm:h-12 flex items-center justify-center bg-[#E8F5E9] rounded-full mb-3 sm:mb-5">
        <i className={`${benefit.icon} text-lg sm:text-2xl text-[#2D5016]`} aria-hidden="true"></i>
      </div>
      <h3 className="text-[0.95rem] sm:text-lg font-bold text-[#1A1A1A] mb-1.5 sm:mb-3">
        {benefit.title}
      </h3>
      <p className="text-xs sm:text-sm text-[#666666] leading-relaxed">{benefit.description}</p>
    </div>
  );
}

/**
 * Main component rendering the value‑proposition section.
 * All translations are safely accessed; fallback to empty strings on error.
 */
export default function ValueProposition() {
  const { t } = useTranslation();

  // Safely build the benefits array – any missing translation will not break the UI
  const benefits: Benefit[] = [
    {
      icon: 'ri-plant-line',
      title: t('value.b1.title', { defaultValue: '' }),
      description: t('value.b1.desc', { defaultValue: '' }),
    },
    {
      icon: 'ri-shield-check-line',
      title: t('value.b2.title', { defaultValue: '' }),
      description: t('value.b2.desc', { defaultValue: '' }),
    },
    {
      icon: 'ri-global-line',
      title: t('value.b3.title', { defaultValue: '' }),
      description: t('value.b3.desc', { defaultValue: '' }),
    },
    {
      icon: 'ri-store-2-line',
      title: t('value.b4.title', { defaultValue: '' }),
      description: t('value.b4.desc', { defaultValue: '' }),
    },
  ];

  return (
    <section
      id="por-que-elegirnos"
      className="py-10 sm:py-20 relative overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/143fbe7ace9c796c3f7c376f7504248f.jpeg)',
        }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(245, 241, 232, 0.75)' }}
        aria-hidden="true"
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-16">
          <p className="text-base sm:text-xl font-semibold text-[#6B8E23] uppercase tracking-wide mb-4 sm:mb-6">
            {t('value.tag', { defaultValue: '' })}
          </p>
          <h2 className="text-[1.4rem] leading-tight sm:text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-2 sm:mb-4">
            {t('value.title', { defaultValue: '' })}
          </h2>
          <p className="text-xs sm:text-lg text-[#555555] max-w-3xl mx-auto">
            {t('value.subtitle', { defaultValue: '' })}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
          {benefits.map((benefit, index) => (
            <AnimatedCard key={index} benefit={benefit} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}
